import { Link } from "react-router-dom";
import { ArrowRight, Compass, Users, BookOpen, MessageSquare } from "lucide-react";
import Reveal from "@/components/Reveal";
import { SMS_URL } from "@/config";

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="mx-auto grid max-w-7xl items-center gap-12 px-6 py-20 md:grid-cols-2 md:px-12 md:py-28">
        <Reveal>
          <p className="font-display text-xs tracking-[0.2em] uppercase text-taupe">
            Thérapie comportementale et cognitive — 10 à 20 ans
          </p>
          <p className="mt-3 text-sm text-stone-500" data-testid="hero-subtitle">
            Spécialisée dans le Développement de l'adolescent en difficultés ou TDAH et troubles.
          </p>
          <h1 className="mt-6 font-display text-4xl font-light leading-tight tracking-tight text-charcoal sm:text-5xl lg:text-6xl">
            Un moteur de Formule 1 avec des freins de vélo&nbsp;?
          </h1>
          <p className="mt-8 max-w-prose text-base leading-relaxed text-stone-700">
            On vous dit distrait, rêveur, instable ou «&nbsp;trop&nbsp;»&nbsp;? Mais si ce n'était
            pas un défaut&nbsp;? De la paralysie devant des devoirs à faire à l'étincelle de
            l'hyperfocus, décodons ensemble un cerveau qui ne s'arrête jamais — pour transformer
            le chaos en clarté.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link
              to="/contact"
              data-testid="hero-contact-cta"
              className="inline-flex items-center gap-2 rounded-full border border-taupe px-7 py-3 font-display text-sm text-taupe transition-colors hover:bg-taupe hover:text-bone"
            >
              Prendre contact <ArrowRight size={16} />
            </Link>
            <Link
              to="/accompagnement"
              data-testid="hero-discover-cta"
              className="inline-flex items-center gap-2 rounded-full border border-taupe px-7 py-3 font-display text-sm text-taupe transition-colors hover:bg-taupe hover:text-bone"
            >
              Découvrir l'accompagnement
            </Link>
            <a
              href={SMS_URL}
              data-testid="hero-agenda-cta"
              className="inline-flex items-center gap-2 rounded-full bg-taupe px-7 py-3 font-display text-sm text-bone transition-colors hover:bg-taupe-dark"
            >
              <MessageSquare size={16} /> Prendre rendez-vous par SMS
            </a>
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="rounded-2xl border border-stone-200 bg-[#F8ECE4] p-6 sm:p-10">
            <img
              src="/logo.png"
              alt="Illustration du logo : un enfant assis, entouré d'une main protectrice"
              data-testid="hero-image"
              className="aspect-square w-full rounded-xl object-cover"
            />
          </div>
        </Reveal>
      </section>

      {/* Pour qui */}
      <section className="bg-sand">
        <div className="mx-auto max-w-7xl px-6 py-24 md:px-12">
          <Reveal>
            <p className="font-display text-xs tracking-[0.2em] uppercase text-stone-500">Pour qui</p>
            <h2 className="mt-4 max-w-2xl font-display text-2xl tracking-tight text-charcoal sm:text-3xl lg:text-4xl">
              Un espace pour les jeunes, un appui pour leurs parents
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-8 md:grid-cols-3">
            {[
              {
                icon: Compass,
                title: "Pour les jeunes",
                text: "De 10 à 20 ans, en difficulté scolaire ou familiale — ou les deux. Un lieu pour comprendre comment fonctionne votre cerveau et trouver vos propres outils.",
                id: "jeunes",
              },
              {
                icon: Users,
                title: "Pour les parents",
                text: "Vous cherchez un soutien suite à un diagnostic — TDAH, HP, multi-DYS, TSA, anxio-dépression, phobie scolaire. Vous repartez avec des clés concrètes pour la maison.",
                id: "parents",
              },
              {
                icon: BookOpen,
                title: "Pour l'école et la famille",
                text: "Favoriser l'inclusion scolaire et familiale, déconstruire les mythes et développer une culture de la neurodiversité, ensemble.",
                id: "inclusion",
              },
            ].map((c, i) => (
              <Reveal key={c.id} delay={i * 0.1}>
                <article
                  data-testid={`home-card-${c.id}`}
                  className="h-full rounded-2xl border border-stone-200 bg-bone p-8 transition-transform duration-300 hover:-translate-y-1"
                >
                  <c.icon size={28} className="text-taupe" strokeWidth={1.5} />
                  <h3 className="mt-6 font-display text-xl text-charcoal">{c.title}</h3>
                  <p className="mt-4 text-sm leading-relaxed text-stone-700">{c.text}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Citation */}
      <section className="mx-auto max-w-7xl px-6 py-24 md:px-12">
        <Reveal>
          <blockquote
            data-testid="home-quote"
            className="max-w-3xl border-l-2 border-taupe pl-8 font-display text-2xl font-light leading-relaxed text-charcoal sm:text-3xl"
          >
            «&nbsp;Expliquer sans stigmatiser, normaliser sans banaliser, outiller sans
            culpabiliser.&nbsp;»
          </blockquote>
          <p className="mt-6 pl-8 text-sm tracking-wide text-stone-500">
            — Juliette Maquestiau, thérapeute comportementale et cognitive
          </p>
        </Reveal>
      </section>

      {/* CTA bandeau */}
      <section className="bg-clay">
        <div className="mx-auto flex max-w-7xl flex-col items-start gap-8 px-6 py-20 md:flex-row md:items-center md:justify-between md:px-12">
          <Reveal>
            <h2 className="max-w-xl font-display text-2xl tracking-tight text-charcoal sm:text-3xl">
              Vous vous reconnaissez dans ces mots&nbsp;? Parlons-en.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <Link
              to="/contact"
              data-testid="cta-band-contact-button"
              className="inline-flex items-center gap-2 rounded-full bg-taupe px-7 py-3 font-display text-sm text-bone transition-colors hover:bg-taupe-dark"
            >
              Écrire à Juliette <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
